// External dependencies
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Internal dependencies
import { getFooterMenu } from '@/modules/cms/client/api/cms.api';

/**
 * The CMS-managed footer link columns.
 *
 * Replaces the columns the footer used to hardcode. Section names and link
 * labels are i18n keys rendered through `t()`: the seeded columns reuse the
 * app's existing keys so translations carry over, and a label an admin types
 * shows as its own text until translated — the same behaviour as every other
 * string in the app, where the key is the English source.
 *
 * Renders `fallback` (the original hardcoded columns) when the menu has not
 * loaded, is empty, or the request failed — so a fresh install or a CMS
 * outage still shows a populated footer rather than a gap.
 */
export default function FooterMenu({ fallback, columnClassName }) {
  const { t } = useTranslation();
  const [sections, setSections] = useState(null);

  useEffect(() => {
    let cancelled = false;

    getFooterMenu()
      .then(data => {
        if (!cancelled && Array.isArray(data)) {
          setSections(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSections(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Not loaded yet, empty, or failed → the original hardcoded columns.
  if (!sections || !sections.length) {
    return fallback || null;
  }

  return (
    <>
      {sections.map(section => (
        <div className={columnClassName} key={section.name}>
          <h3 className="font-brand-light">{t(section.name)}</h3>
          <ul className="list-unstyled home-footer-pages font-brand-light">
            {section.items.map(item => (
              <li key={`${item.url}-${item.label}`}>
                <a href={item.url}>{t(item.label)}</a>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}

FooterMenu.propTypes = {
  // The original hardcoded columns, shown until (or unless) the CMS menu
  // loads. Keeps the footer populated on a fresh install or an API failure.
  fallback: PropTypes.node,
  // Bootstrap grid classes for each column, matched to the footer it sits in.
  columnClassName: PropTypes.string,
};

FooterMenu.defaultProps = {
  fallback: null,
  columnClassName: 'col-sm-6 col-md-3',
};
